import { useCallback, useEffect, useRef } from "react";

/* Impression tracking: "did this element actually reach the user's screen?".
 *
 * Written for the insight rail's InsightShown event (OPSS-208), whose schema
 * asks for viewport entry rather than render — a card mounted below the fold has
 * not been shown to anyone, and counting it would understate every apply/dismiss
 * rate measured against it. */

/** Fraction of the element that must be visible to count as shown. Half, so a
 *  one-pixel sliver at the edge of the viewport is not an impression, but a card
 *  taller than the viewport still is. */
const VISIBLE_FRACTION = 0.5;

/** Calls `onVisible` once, the first time the observed element is at least
 *  half in the viewport, then stops observing it.
 *
 *  Returns a ref callback (not a ref object) so it re-attaches correctly when
 *  the element is conditionally rendered or replaced in a list.
 *
 *  Fires at most once per mount; de-duplicating across mounts is the caller's
 *  job, since only the caller knows what identity to dedupe on.
 *
 *  Where `IntersectionObserver` is unavailable (jsdom, ancient browsers) it
 *  degrades to firing on attach: losing the event entirely would silently drop
 *  the funnel's denominator, whereas over-reporting is visible in the data. */
export function useOnVisible(
  onVisible: () => void,
): (node: Element | null) => void {
  // The latest callback, so a re-render with a new closure doesn't tear down and
  // re-create the observer (which would re-fire on an element already seen).
  const latest = useRef(onVisible);
  useEffect(() => {
    latest.current = onVisible;
  });

  const observer = useRef<IntersectionObserver | null>(null);
  // A latch, not just a disconnect: an entry already queued when we disconnect
  // would otherwise report a second impression for the same element.
  const fired = useRef(false);

  useEffect(() => () => observer.current?.disconnect(), []);

  return useCallback((node: Element | null) => {
    observer.current?.disconnect();
    observer.current = null;
    if (!node) return;

    fired.current = false;

    if (typeof IntersectionObserver === "undefined") {
      fired.current = true;
      latest.current();
      return;
    }

    observer.current = new IntersectionObserver(
      (entries) => {
        if (fired.current) return;

        if (!entries.some((entry) => entry.isIntersecting)) return;

        fired.current = true;
        observer.current?.disconnect();
        observer.current = null;
        latest.current();
      },
      { threshold: VISIBLE_FRACTION },
    );
    observer.current.observe(node);
  }, []);
}
